import { BodyM, Button, Caption, Description, Display, Heading4 } from '@casavo/base-ui'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'

import Icon from '@/components/Icon'
import { useAuthentication } from '@/context/AuthProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import useGoogleAnalyticsIds from '@/hooks/useGoogleAnalyticsIds'
import { sendSnowplowClientSideEvent } from '@/lib/tracking/snowplow/client'
import { eventSchemas, isSnowplowEnabled } from '@/lib/tracking/snowplow/common'
import { UserValuation } from '@/pages/profile/suggested'
import { formatDate } from '@/utils/date'
import { formatPrice } from '@/utils/format-currency'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { MortgageBannerValuationModal } from './MortgageBannerValuation/Modal'
import { addressContainer, addressIconContainer, minMaxPriceContainer, orangeBox, pricesDivider } from './styles.css'

export const SuggestedListingPrice: React.FC<{
  onSuggestedListingPriceClickHandler: () => void
  userValuation: UserValuation
  userValuationSessionId: string
}> = ({ onSuggestedListingPriceClickHandler, userValuation, userValuationSessionId }) => {
  const { lang, t } = useTranslation()
  const { googleClientId } = useGoogleAnalyticsIds()
  const { user } = useAuthentication()
  const { address, performed_at, price_range } = userValuation

  return (
    <div className={sprinkles({ padding: { desktop: 'xl' }, paddingTop: { mobile: 'l' } })}>
      <div className={sprinkles({ paddingBottom: 'l' })}>
        <BodyM>{t('suggested:ValuationModal_Title_1')}</BodyM>
      </div>
      <div className={cn([addressIconContainer])}>
        <div className={sprinkles({ paddingRight: 's-m' })}>
          <Icon.House />
        </div>
        <div className={cn([addressContainer])}>
          <Description>
            {t('common:address_format', {
              city: address.city,
              streetName: address.street,
              streetNumber: address.number,
            })}
          </Description>
        </div>
      </div>
      <div className={sprinkles({ padding: 'm', textAlign: 'center' })}>
        <Display>{formatPrice({ cents: price_range.average * 100 }, lang)}</Display>
        <div className={cn([minMaxPriceContainer])}>
          <div className={sprinkles({ paddingY: 's-m' })}>
            <Heading4 strong>{formatPrice({ cents: price_range.min * 100 }, lang)}</Heading4>
          </div>
          <div className={cn([pricesDivider])}></div>
          <div className={sprinkles({ paddingY: 's-m' })}>
            <Heading4 strong>{formatPrice({ cents: price_range.max * 100 }, lang)}</Heading4>
          </div>
        </div>
        <Caption color="greyscale300">
          {t('suggested:ValuationModal_DateDescription', {
            'valuation-date': formatDate('short', lang, performed_at),
          })}
        </Caption>
      </div>

      {isLocaleEnabledFor('mortgageValuation', lang) ? (
        <MortgageBannerValuationModal
          onBannerCtaClick={() => {
            handleClick(GTMEventCategory.SuggestedPage, 'MortgageBannerValuationCta', 'Modal')
            if (isSnowplowEnabled) {
              sendSnowplowClientSideEvent({
                data: {
                  googleClientId: googleClientId,
                  location: 'modal',
                  userId: user?.casavo_user_id,
                  valuationSessionId: userValuationSessionId,
                },
                schema: eventSchemas.almostThereMortgageRequestCtaClick,
              })
            }
            onSuggestedListingPriceClickHandler()
          }}
        />
      ) : (
        <div className={sprinkles({ padding: 'm' })}>
          <div className={cn([orangeBox])}>
            <div className={sprinkles({ marginBottom: 's-m' })}>
              <BodyM strong>{t('suggested:ValuationModal_CardTitle')}</BodyM>
            </div>
            <Description>{t('suggested:ValuationModal_CardDescription')}</Description>
            <Button
              fullWidth
              className={sprinkles({ marginTop: '2xl' })}
              label={t('suggested:ValuationModal_CardButton')}
              onClick={() => {
                onSuggestedListingPriceClickHandler()
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
